import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, ExternalLink, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const JogoBichoPage = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const [currentDate] = useState(new Date().toLocaleDateString('pt-BR'));

  // Mock data for the results
  const resultados = {
    riodejaneiro: [
      { horario: "11:00", resultado: "7458", bicho: "Carneiro (14)", grupo: "14" },
      { horario: "14:00", resultado: "2391", bicho: "Vaca (25)", grupo: "25" },
      { horario: "16:00", resultado: "5128", bicho: "Leão (07)", grupo: "07" },
      { horario: "18:00", resultado: "9362", bicho: "Porco (18)", grupo: "18" },
      { horario: "21:00", resultado: "4017", bicho: "Águia (05)", grupo: "05" }
    ],
    saopaulo: [
      { horario: "11:20", resultado: "1234", bicho: "Borboleta (09)", grupo: "09" },
      { horario: "14:20", resultado: "5678", bicho: "Elefante (12)", grupo: "12" },
      { horario: "16:20", resultado: "9012", bicho: "Galo (13)", grupo: "13" },
      { horario: "18:20", resultado: "3456", bicho: "Gato (14)", grupo: "14" },
      { horario: "21:20", resultado: "7890", bicho: "Jacaré (15)", grupo: "15" }
    ],
    bahia: [
      { horario: "10:45", resultado: "2468", bicho: "Cabra (17)", grupo: "17" },
      { horario: "13:45", resultado: "1357", bicho: "Coelho (04)", grupo: "04" },
      { horario: "15:45", resultado: "8024", bicho: "Cavalo (11)", grupo: "11" },
      { horario: "17:45", resultado: "6135", bicho: "Cobra (10)", grupo: "10" },
      { horario: "20:45", resultado: "9753", bicho: "Macaco (21)", grupo: "21" }
    ]
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRefresh = () => {
    toast.info("Atualizando resultados...");
    setTimeout(() => {
      toast.success("Resultados atualizados!");
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Resultado Jogo do Bicho
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Confira os últimos resultados do Jogo do Bicho. Atualizações diárias dos principais sorteios.
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader className="bg-muted/50">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-xl">Resultados do dia {currentDate}</CardTitle>
                <CardDescription>
                  Confira os sorteios das principais praças
                </CardDescription>
              </div>
              <Button variant="outline" onClick={handleRefresh} className="flex gap-2 items-center">
                <RefreshCw size={16} /> Atualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <Tabs defaultValue="riodejaneiro">
              <TabsList className="grid grid-cols-3 mb-6">
                <TabsTrigger value="riodejaneiro">Rio de Janeiro</TabsTrigger>
                <TabsTrigger value="saopaulo">São Paulo</TabsTrigger>
                <TabsTrigger value="bahia">Bahia</TabsTrigger>
              </TabsList>
              
              <TabsContent value="riodejaneiro">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left p-3 font-medium">Horário</th>
                        <th className="text-left p-3 font-medium">Resultado</th>
                        <th className="text-left p-3 font-medium">Bicho</th>
                        <th className="text-left p-3 font-medium">Grupo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultados.riodejaneiro.map((item, index) => (
                        <tr key={index} className="border-t border-border">
                          <td className="p-3 flex items-center"><Clock size={14} className="mr-1 text-muted-foreground" /> {item.horario}</td>
                          <td className="p-3 font-semibold">{item.resultado}</td>
                          <td className="p-3">{item.bicho}</td>
                          <td className="p-3">{item.grupo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
              
              <TabsContent value="saopaulo">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left p-3 font-medium">Horário</th>
                        <th className="text-left p-3 font-medium">Resultado</th>
                        <th className="text-left p-3 font-medium">Bicho</th>
                        <th className="text-left p-3 font-medium">Grupo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultados.saopaulo.map((item, index) => (
                        <tr key={index} className="border-t border-border">
                          <td className="p-3 flex items-center"><Clock size={14} className="mr-1 text-muted-foreground" /> {item.horario}</td>
                          <td className="p-3 font-semibold">{item.resultado}</td>
                          <td className="p-3">{item.bicho}</td>
                          <td className="p-3">{item.grupo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
              
              <TabsContent value="bahia">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left p-3 font-medium">Horário</th>
                        <th className="text-left p-3 font-medium">Resultado</th>
                        <th className="text-left p-3 font-medium">Bicho</th>
                        <th className="text-left p-3 font-medium">Grupo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultados.bahia.map((item, index) => (
                        <tr key={index} className="border-t border-border">
                          <td className="p-3 flex items-center"><Clock size={14} className="mr-1 text-muted-foreground" /> {item.horario}</td>
                          <td className="p-3 font-semibold">{item.resultado}</td>
                          <td className="p-3">{item.bicho}</td>
                          <td className="p-3">{item.grupo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <Card>
            <CardHeader>
              <CardTitle>Resultado Jogo do Bicho</CardTitle>
              <CardDescription>Site oficial de resultados</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Acesse o site oficial para ver todos os resultados detalhados, histórico de sorteios e estatísticas do jogo do bicho.
              </p>
              <div className="flex justify-between">
                <Button 
                  asChild 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a 
                    href="https://resultadojogodobicho.net/rio-de-janeiro/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={16} className="mr-2" /> Acessar Site
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleCopy("https://resultadojogodobicho.net/rio-de-janeiro/")}
                  className={copied === "https://resultadojogodobicho.net/rio-de-janeiro/" ? "bg-primary/10" : ""}
                >
                  <Copy size={16} className="mr-1" /> 
                  {copied === "https://resultadojogodobicho.net/rio-de-janeiro/" ? "Copiado!" : "Copiar Link"}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Canal no Rumble</CardTitle>
              <CardDescription>Mestre do Jogo do Bicho</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Acesse nosso canal no Rumble com dicas, análises e estratégias para o jogo do bicho. Conteúdo exclusivo para apostadores.
              </p>
              <div className="flex justify-between">
                <Button 
                  asChild 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a 
                    href="https://rumble.com/c/c-656703" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={16} className="mr-2" /> Acessar Canal
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleCopy("https://rumble.com/c/c-656703")}
                  className={copied === "https://rumble.com/c/c-656703" ? "bg-primary/10" : ""}
                >
                  <Copy size={16} className="mr-1" /> 
                  {copied === "https://rumble.com/c/c-656703" ? "Copiado!" : "Copiar Link"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-8">
          <CardHeader className="bg-muted/50">
            <CardTitle>Tabela dos Animais</CardTitle>
            <CardDescription>
              Confira os 25 animais do jogo do bicho e seus respectivos grupos
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {[
                { grupo: "01", animal: "Avestruz" },
                { grupo: "02", animal: "Águia" },
                { grupo: "03", animal: "Burro" },
                { grupo: "04", animal: "Borboleta" },
                { grupo: "05", animal: "Cachorro" },
                { grupo: "06", animal: "Cabra" },
                { grupo: "07", animal: "Carneiro" },
                { grupo: "08", animal: "Camelo" },
                { grupo: "09", animal: "Cobra" },
                { grupo: "10", animal: "Coelho" },
                { grupo: "11", animal: "Cavalo" },
                { grupo: "12", animal: "Elefante" },
                { grupo: "13", animal: "Galo" },
                { grupo: "14", animal: "Gato" },
                { grupo: "15", animal: "Jacaré" },
                { grupo: "16", animal: "Leão" },
                { grupo: "17", animal: "Macaco" },
                { grupo: "18", animal: "Porco" },
                { grupo: "19", animal: "Pavão" },
                { grupo: "20", animal: "Peru" },
                { grupo: "21", animal: "Touro" },
                { grupo: "22", animal: "Tigre" },
                { grupo: "23", animal: "Urso" },
                { grupo: "24", animal: "Veado" },
                { grupo: "25", animal: "Vaca" }
              ].map((item, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-3 text-center">
                  <div className="font-bold">{item.grupo}</div>
                  <div className="text-sm text-muted-foreground">{item.animal}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-4">
            * Este site é apenas informativo e não realiza apostas. O jogo do bicho é uma loteria tradicional no Brasil.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default JogoBichoPage;